import { useState } from "react";
import {
  ThumbsDownIcon,
  ThumbsUpIcon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  IconButton,
  IconButtonSize,
  IconButtonVariant
} from "shared/components/IconButton";
import { Button, ButtonSize, ButtonVariant, TextArea } from "shared/components";
import { Show } from "shared/components/helpers/Show";
import { cx } from "shared/utils";
import type { BoomHelpFeedbackReason } from "api/boom-help/types";
import { BOOM_HELP_COPY, FEEDBACK_REASONS } from "../constants";
import type { BoomHelpEntry } from "../store";
import { useRateBoomHelpMessage } from "../use-boom-help-actions";

const ACTIVE_THUMB_CLASS = "!text-primary-500 !stroke-primary-500";

const FeedbackForm = ({
  messageId,
  onSend
}: {
  messageId: number;
  onSend: (reason: BoomHelpFeedbackReason, comment?: string) => void;
}) => {
  const [reason, setReason] = useState<BoomHelpFeedbackReason | null>(null);
  const [comment, setComment] = useState("");

  const pick = (value: BoomHelpFeedbackReason) => {
    setReason(value);
    if (value !== "other") onSend(value);
  };

  return (
    <div className="space-y-3 rounded-lg border border-alpha-100 bg-white p-4 shadow-xs">
      <p className="text-sm font-bold text-content-1000">
        {BOOM_HELP_COPY.feedbackPrompt}
      </p>
      <div className="flex flex-wrap gap-2">
        {FEEDBACK_REASONS.map((option) => (
          <button
            key={option.value}
            type="button"
            aria-pressed={reason === option.value}
            onClick={() => pick(option.value)}
            className={cx(
              "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
              reason === option.value
                ? "border-primary-500 bg-primary-20 text-primary-600"
                : "border-alpha-100 text-content-500 hover:bg-alpha-40"
            )}
          >
            {option.label}
          </button>
        ))}
      </div>
      <Show if={reason === "other"}>
        <TextArea
          id={`boom-help-comment-${messageId}`}
          placeholder={BOOM_HELP_COPY.feedbackPlaceholder}
          value={comment}
          maxLength={1000}
          onChange={(event) => setComment(event.target.value)}
          rows={2}
          hideOptional
          legacyErrorMargins={false}
        />
        <div className="flex justify-end">
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            disabled={!comment.trim()}
            onClick={() => onSend("other", comment.trim())}
          >
            {BOOM_HELP_COPY.feedbackSend}
          </Button>
        </div>
      </Show>
    </div>
  );
};

export const FeedbackControls = ({ message }: { message: BoomHelpEntry }) => {
  const { mutate, isPending } = useRateBoomHelpMessage();
  const [asking, setAsking] = useState(false);
  const [sent, setSent] = useState(false);

  const rateDown = () => {
    mutate({ messageId: message.id, rating: "thumbs_down" });
    setAsking(true);
    setSent(false);
  };

  const sendReason = (reason: BoomHelpFeedbackReason, comment?: string) => {
    mutate({ messageId: message.id, rating: "thumbs_down", reason, comment });
    setAsking(false);
    setSent(true);
  };

  return (
    <>
      <div className="flex items-center gap-1">
        <IconButton
          aria-label={BOOM_HELP_COPY.helpful}
          tooltip={BOOM_HELP_COPY.helpful}
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.XS}
          HugeIcon={ThumbsUpIcon}
          disabled={isPending}
          onClick={() => {
            setAsking(false);
            mutate({ messageId: message.id, rating: "thumbs_up" });
          }}
          className={
            message.rating === "thumbs_up" ? ACTIVE_THUMB_CLASS : undefined
          }
        />
        <IconButton
          aria-label={BOOM_HELP_COPY.notHelpful}
          tooltip={BOOM_HELP_COPY.notHelpful}
          variant={IconButtonVariant.TEXT_BASE}
          size={IconButtonSize.XS}
          HugeIcon={ThumbsDownIcon}
          disabled={isPending}
          onClick={rateDown}
          className={
            message.rating === "thumbs_down" ? ACTIVE_THUMB_CLASS : undefined
          }
        />
      </div>
      <Show if={asking}>
        <FeedbackForm messageId={message.id} onSend={sendReason} />
      </Show>
      <Show if={sent}>
        <p className="text-xs font-medium text-content-300">
          {BOOM_HELP_COPY.feedbackThanks}
        </p>
      </Show>
    </>
  );
};
